import { produce, immerable, castDraft } from "immer";
import { VendorDataCollection } from "../../../vendor-data/models";
import { LensService } from "../../domain/services/LensService";
import { LensGroupInfoService } from "../../domain/services/LensGroupInfoService";
import { type GetLensV2Query } from "../../api/query";
import { RangeModifier } from "../../../connections/models/RangeModifier";
import { BaseModel } from "../../../core/models";
import { type LensInAddLensGroupInfo, type AddLensGroupInfo } from "../../../groups/components/add-to-lensgroup-modal";
import { LensGroupInfo } from "./LensGroupInfo";
import { LensModel } from "./LensModel";

export class GlobalLensDetailsModel {
    public [immerable] = true;
    public readonly groups = new Map<string | null, LensGroupInfo>();
    public readonly lens: LensModel = BaseModel.createInstance(LensModel);

    public static createFromLensWithConnections(lensItem: GetLensV2Query["getLensV2"]) {
        return BaseModel.createInstance(GlobalLensDetailsModel, {
            groups: new Map(
                (lensItem?.connections ?? []).map((connection) => [
                    connection!.group!.id ?? null,
                    LensGroupInfoService.createFromConnection(connection),
                ]),
            ),
            lens: LensService.createFromLens(lensItem?.lens),
        });
    }

    private static getMaxPriority(lenses: readonly LensInAddLensGroupInfo[]): number {
        return lenses.reduce((prevPriority, nextLens) => Math.max(prevPriority, nextLens.priority), 0);
    }

    public updateVendorData(
        groupId: string,
        reducer: (vendorData: VendorDataCollection) => VendorDataCollection,
    ): GlobalLensDetailsModel {
        return produce(this, (draft) => {
            const group = draft.groups.get(groupId);
            if (!group) return;
            castDraft(group).vendorData = castDraft(reducer(group.vendorData));
            castDraft(group).isUpdated = true;
        });
    }

    public updateGroups(newGroups: readonly LensGroupInfo[]): GlobalLensDetailsModel {
        return produce(this, (draft) => {
            draft.groups = castDraft(new Map(newGroups.map((group) => [group.groupId, group])));
            draft.hasChanged = false;
        });
    }

    public updateSchedule(groupId: string, scheduleRange: RangeModifier): GlobalLensDetailsModel {
        return produce(this, (draft) => {
            const group = draft.groups.get(groupId);
            if (group) {
                group.scheduleRange = scheduleRange;
                group.isUpdated = true;
            }
        });
    }

    public addGroups(groupsToAdd: AddLensGroupInfo[]) {
        return produce(this, (draft) => {
            groupsToAdd.forEach(({ id, name, totalNumLenses, lenses }) => {
                if (draft.groups.has(id)) {
                    draft.groups.get(id)!.isRemoved = false;
                } else {
                    const maxPriority = GlobalLensDetailsModel.getMaxPriority(lenses);
                    draft.groups.set(
                        id,
                        castDraft(
                            BaseModel.createInstance(LensGroupInfo, {
                                groupId: id,
                                name,
                                numberOfLenses: totalNumLenses,
                                isNew: true,
                                priority: maxPriority + 1,
                            }),
                        ),
                    );
                }
            });
        });
    }

    public removeGroup(groupId: string) {
        return produce(this, (draft) => {
            const group = draft.groups.get(groupId);
            if (group) {
                group.isRemoved = true;
            }
        });
    }

    public commitChanges() {
        return produce(this, (draft) => {
            draft.groups.forEach((group, groupId) => {
                if (group.isRemoved) {
                    draft.groups.delete(groupId);
                    return;
                }

                group.isNew = false;
                group.isUpdated = false;
                group.vendorData = castDraft(group.vendorData.replaceVendorData(group.vendorData.toDataObjects()));
            });
        });
    }

    public get newOrUpdatedGroups() {
        return Array.from(this.groups.values()).filter((group) => !group.isRemoved && (group.isNew || group.isUpdated));
    }

    public get removedGroupsIds() {
        return Array.from(this.groups.values())
            .filter((group) => group.isRemoved && !group.isNew)
            .map((group) => group.groupId);
    }

    public get visibleGroups(): readonly LensGroupInfo[] {
        return Array.from(this.groups.values()).filter((group) => !group.isRemoved);
    }

    public get isValid() {
        return Array.from(this.groups.values()).every(({ vendorData }) => vendorData.isCollectionValid);
    }

    public get groupIds() {
        return Array.from(this.groups.values())
            .filter((group) => !group.isRemoved)
            .map((group) => group.groupId);
    }

    public get hasChanged() {
        return Array.from(this.groups.values()).some((group) => group.isUpdated || group.isNew || group.isRemoved);
    }
}
